import '@/i18n';
import i18n, { detectLanguageFromIP } from '@/i18n';
import { useEffect, Suspense, lazy } from 'react';
import { LazyMotion, domAnimation } from 'framer-motion';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Route, Switch, Router as WouterRouter, useLocation } from 'wouter';
import { ALL_LANG_CODES, LangContext, isRtlLang, langUrlPrefix } from '@/lib/i18n-routing';
import { getConsent, initAnalytics, trackPageView } from '@/lib/analytics';
import CookieConsent from '@/components/CookieConsent';

// Home is the highest-traffic entry point (and is prerendered) so it stays a static
// import; everything else is code-split out of the main bundle to keep first-load JS
// small -- these pages ship on demand instead of on every visit.
const Home = lazy(() => import('@/pages/Home'));
const NotFound = lazy(() => import('@/pages/not-found'));
const Admin = lazy(() => import('@/pages/Admin'));
const ExchangePage = lazy(() => import('@/pages/ExchangePage'));
const Guides = lazy(() => import('@/pages/Guides'));
const GuideArticle = lazy(() => import('@/pages/GuideArticle'));
const HowWeMakeMoney = lazy(() => import('@/pages/HowWeMakeMoney'));
const ContentPage = lazy(() => import('@/pages/ContentPage'));
const FeeCalculator = lazy(() => import('@/pages/FeeCalculator'));
const TrustSafety = lazy(() => import('@/pages/TrustSafety'));
const Compare = lazy(() => import('@/pages/Compare'));

const queryClient = new QueryClient();

// Static localized content pages rendered by the shared ContentPage component.
// Keep in sync with entry-server.tsx, scripts/prerender.mjs and generate-sitemap.mjs.
const CONTENT_PAGES = [
  { ns: 'about', path: '/about', showContact: true },
  { ns: 'privacyPolicy', path: '/privacy-policy', showContact: false },
  { ns: 'termsOfService', path: '/terms-of-service', showContact: false },
] as const;

// Client-only analytics: initializes GA4 once, then records a virtual page view on
// every SPA route change. No-ops when VITE_GA_MEASUREMENT_ID is unset.
function Analytics() {
  const [location] = useLocation();
  useEffect(() => {
    // Only load GA if the visitor already accepted cookies on a previous visit.
    // First-time visitors are gated behind the CookieConsent banner instead.
    if (getConsent() === 'accepted') {
      initAnalytics();
    }
  }, []);
  useEffect(() => {
    trackPageView(window.location.pathname + window.location.search);
  }, [location]);
  return null;
}

// Wouter (unlike a browser's native navigation) preserves scroll position across SPA
// route changes, so navigating from a scrolled-down list to a new page would leave the
// new page scrolled to the bottom. Reset to the top on every path change.
function ScrollToTop() {
  const [location] = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);
  return null;
}

const EXCHANGE_IDS = ['bitget', 'mexc', 'bybit', 'okx', 'gateio', 'binance'];

// Wraps a page for a given language: sets the active i18n language to match the URL
// (English pages skip auto IP-detection since that's the "undecided" default; a
// language-prefixed URL is an explicit signal that should never be overridden) and
// provides LangContext so pages/components can build same-language internal links.
function LocalizedPage({ lang, children }: { lang: string; children: React.ReactNode }) {
  useEffect(() => {
    if (lang === 'en') {
      detectLanguageFromIP();
    } else if (i18n.language.split('-')[0] !== lang) {
      i18n.changeLanguage(lang);
    }
    // Keep <html lang>/<html dir> correct after client-side navigation. The
    // prerendered HTML already carries both, but wouter route changes do not
    // reload the document, so without this an Arabic or Urdu page reached by
    // clicking a link would keep the previous page's direction.
    if (typeof document !== 'undefined') {
      document.documentElement.lang = lang;
      document.documentElement.dir = isRtlLang(lang) ? 'rtl' : 'ltr';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  return <LangContext.Provider value={lang}>{children}</LangContext.Provider>;
}

/**
 * The catch-all route had no LocalizedPage around it, so a 404 never ran the
 * effect that sets <html lang> and <html dir> and never provided LangContext.
 * An Arabic or Urdu 404 laid itself out left-to-right, and every link on the
 * page lost its language prefix because the context fell back to 'en'.
 *
 * The language cannot come from a route param here — the path matched nothing —
 * so it comes from the URL's first segment.
 */
function NotFoundRoute() {
  const [location] = useLocation();
  const first = location.split('/')[1] ?? '';
  const lang = (ALL_LANG_CODES as readonly string[]).includes(first) ? first : 'en';
  return (
    <LocalizedPage lang={lang}>
      <NotFound />
    </LocalizedPage>
  );
}

function Router() {
  return (
    <Suspense fallback={null}>
      <Switch>
        <Route path="/admin" component={Admin} />
        {ALL_LANG_CODES.flatMap((lang) => {
          const p = langUrlPrefix(lang);
          return [
            <Route key={`${lang}-home`} path={p === '' ? '/' : p}>
              <LocalizedPage lang={lang}>
                <Home />
              </LocalizedPage>
            </Route>,
            <Route key={`${lang}-guides`} path={`${p}/guides`}>
              <LocalizedPage lang={lang}>
                <Guides />
              </LocalizedPage>
            </Route>,
            <Route key={`${lang}-guide-slug`} path={`${p}/guides/:slug`}>
              {(params: { slug: string }) => (
                <LocalizedPage lang={lang}>
                  <GuideArticle slug={params.slug} />
                </LocalizedPage>
              )}
            </Route>,
            <Route key={`${lang}-how-we-make-money`} path={`${p}/how-we-make-money`}>
              <LocalizedPage lang={lang}>
                <HowWeMakeMoney />
              </LocalizedPage>
            </Route>,
            <Route key={`${lang}-fee-calculator`} path={`${p}/fee-calculator`}>
              <LocalizedPage lang={lang}>
                <FeeCalculator />
              </LocalizedPage>
            </Route>,
            <Route key={`${lang}-security`} path={`${p}/security`}>
              <LocalizedPage lang={lang}>
                <TrustSafety />
              </LocalizedPage>
            </Route>,
            <Route key={`${lang}-compare`} path={`${p}/compare`}>
              <LocalizedPage lang={lang}>
                <Compare />
              </LocalizedPage>
            </Route>,
            ...CONTENT_PAGES.map((page) => (
              <Route key={`${lang}-${page.ns}`} path={`${p}${page.path}`}>
                <LocalizedPage lang={lang}>
                  <ContentPage ns={page.ns} path={page.path} showContact={page.showContact} />
                </LocalizedPage>
              </Route>
            )),
            ...EXCHANGE_IDS.map((id) => (
              <Route key={`${lang}-${id}`} path={`${p}/${id}`}>
                <LocalizedPage lang={lang}>
                  <ExchangePage exchangeId={id} />
                </LocalizedPage>
              </Route>
            )),
          ];
        })}
        <Route component={NotFoundRoute} />
      </Switch>
    </Suspense>
  );
}

function App() {
  return (
    <ThemeProvider>
    <QueryClientProvider client={queryClient}>
      <LazyMotion features={domAnimation}>
        <WouterRouter base={import.meta.env.BASE_URL.replace(/\/$/, '')}>
          <Analytics />
          <ScrollToTop />
          <Router />
        </WouterRouter>
        <CookieConsent />
      </LazyMotion>
    </QueryClientProvider>
    </ThemeProvider>
  );
}

export default App;
